import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import type { EnterpriseMemberDetailResponse } from '@/api/teamMembersTable'
import { cn } from '@/lib/utils'
import { formatLastUsedAt } from '@/pages/app/UsageV2/TeamMembersTab/relativeDate'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { SettingType } from '@/types'

interface MemberDetailHeaderProps {
  member: EnterpriseMemberDetailResponse | undefined
  actionsSlot?: React.ReactNode
}

const fullName = (m: EnterpriseMemberDetailResponse | undefined): string => {
  if (!m) return ''
  const first = m.firstName ?? ''
  const last = m.lastName ?? ''
  const joined = `${first} ${last}`.trim()
  return joined || m.email || m.userId
}

const formatJoinedAt = (unixSeconds: number | null): string => {
  if (unixSeconds === null) return '—'
  return format(new Date(unixSeconds * 1000), 'MMM d, yyyy')
}

const StatusBadge = ({ status }: { status: 'active' | 'trialing' | undefined }) => {
  if (!status) return null
  const isActive = status === 'active'
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-md px-2 py-0.5 font-[550] text-[12px] leading-[18px]',
        isActive ? 'bg-[var(--green-100)] text-[var(--green-600)]' : 'bg-amber-100 text-amber-700'
      )}
    >
      {isActive ? 'Active' : 'Trialing'}
    </span>
  )
}

const MemberDetailHeader = ({ member, actionsSlot }: MemberDetailHeaderProps) => {
  const enterprise = useSelector(selectCurrentEnterprise)
  const showLastUsedAt = enterprise?.settings?.[SettingType.showLastUsedAt] === true
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-3">
            <h1 className="m-0 !font-[Figtree] text-2xl !font-[600] leading-8 tracking-[-0.01em] text-foreground">
              {fullName(member)}
            </h1>
            <StatusBadge status={member?.status} />
          </div>
          <div className="text-foreground text-[15px] leading-5">{member?.email ?? ''}</div>
        </div>
        {actionsSlot ? <div className="flex flex-wrap items-center gap-3">{actionsSlot}</div> : null}
      </div>
      <div className="text-foreground flex flex-wrap items-center gap-3 text-base font-[550] leading-6">
        <span>Joined {formatJoinedAt(member?.joinedAt ?? null)}</span>
        {showLastUsedAt && (
          <>
            <span aria-hidden="true">|</span>
            <span>Last used {formatLastUsedAt(member?.lastUsedAt ?? null, { sentenceCase: true })}</span>
          </>
        )}
      </div>
    </div>
  )
}

export default MemberDetailHeader
